/**
  * Dropdown component Events and Methods
  *
  * @param {element} element - Dropdown element
  */
class Dropdown {
  constructor(element) {
    this.dropdown = element;
    element.__dropdown = this; // lets the shared arrow-key navigator below reuse dropdownShow/Hide/recalcMenuHeight instead of duplicating them
    this.toggleCTA = element.querySelector('.dropdown-toggle');
    this.supportedEvents = ["click", "mouseenter", "mouseleave", "focusin", "focusout"];
    this.touchStartY = 0;
    this.touchEndY = 0;
    this.touchThreshold = 10;
    if (!this.toggleCTA) return; // missing toggle
    this.init();
  }

  init() {
    if ('ontouchstart' in window) {
      this.dropdown.addEventListener('touchstart', (evt) => {
        this.touchStartY = evt.touches[0].clientY;
      });

      this.dropdown.addEventListener('touchend', (evt) => {
        this.touchEndY = evt.changedTouches[0].clientY;
        const touchDiff = Math.abs(this.touchEndY - this.touchStartY);
        if (touchDiff < this.touchThreshold && evt.target.closest('.dropdown-toggle')) {
          evt.preventDefault();
          evt.stopImmediatePropagation();
          this.toggleDropdown(evt);
        }
      });
    }

    this.supportedEvents.forEach((type) => {
      if (type === 'click') {
        this.toggleCTA.addEventListener(type, (evt) => {
          evt.preventDefault();evt.stopImmediatePropagation();
          let a_Tag = evt.target.closest('a');
          if(a_Tag && a_Tag.href != "#"){
            window.location.href = a_Tag.href;
          }
          this.toggleDropdown(evt);
        });
      } else if(type === 'mouseenter') {
        this.toggleCTA.addEventListener(type, (evt) => {
          evt.stopImmediatePropagation();
          let navElement = evt.target.closest('nav');
          if(navElement.classList.contains('navbar-mobile'))return;
            this.dropdownShow(this.dropdown);
           // Skip if the dropdown has gfr class
            if (this.dropdown.querySelector('.dropdown-menu--gfr')) return;
            const firstSubmenuItem = this.dropdown.querySelector('.dropdown-menu--submenu > .nav-item');
            if (firstSubmenuItem && firstSubmenuItem.querySelector(':scope > .dropdown-menu')) {
              // Pre-size the panel for the first category's content (mouse-only
              // preview) and reuse the same height math dropdownShow() already
              // runs for whichever item actually ends up open.
              this.recalcMenuHeight(firstSubmenuItem);
              firstSubmenuItem.classList.add('first-open');
            }
        });
        this.toggleCTA.addEventListener(type, (_evt) => {
          this.childOnFocusChange(this.dropdown);
        });
      } else if(type === 'focusin') {
        this.dropdown.addEventListener(type, (evt) => {
          let navElement = evt.target.closest('nav');

          if (navElement?.classList.contains('navbar-mobile')) return;

          // Focus alone must never open the panel (ADA) — only explicit
          // activation (Enter/Space/Arrow) does, handled in the keydown
          // listener below. Still close sibling dropdowns on focus-in.
          this.childOnFocusChange(this.dropdown);
        });
        this.toggleCTA.addEventListener('keydown', (evt) => {
          let navElement = evt.target.closest('nav');
          if (navElement?.classList.contains('navbar-mobile')) {
            // Legacy behavior, untouched — mobile isn't part of the ADA fix.
            if (evt.key === ' ' || evt.key === 'Escape') {
              evt.preventDefault();
              this.toggleDropdown(evt);
            }
            return;
          }

          if (evt.key === ' ' || evt.key === 'Enter') {
            // Enter/Space open (or close) the panel — never navigate the
            // trigger's own href from the keyboard (ADA: activation opens,
            // it doesn't follow the link). Call dropdownShow/Hide directly
            // instead of toggleDropdown(), which delays via a 50ms
            // setTimeout meant for click/touch — a keyboard user can Tab
            // again well inside that window, and while .open hasn't landed
            // yet the panel is visibility:hidden and gets skipped in tab
            // order, slamming it shut on the very next Tab.
            evt.preventDefault();
            if (this.dropdown.classList.contains('open')) {
              this.dropdownHide(this.dropdown, 'keydown');
            } else {
              this.dropdownShow(this.dropdown);
            }
          }
          // Escape is handled by the delegated listener below (covers the
          // trigger too, since it bubbles from this.toggleCTA).
        });
      } else if(type === 'mouseleave') {
        this.dropdown.addEventListener(type, (evt) => {
           let navElement = evt.target.closest('nav');
          if(navElement.classList.contains('navbar-mobile'))return;
          // A layout shift while the panel's height transitions open can
          // fire a phantom mouseleave with no real pointer movement. Don't
          // let that yank the menu shut out from under active keyboard nav.
          if (this.dropdown.contains(document.activeElement)) return;
          this.dropdownHide(this.dropdown,'mouseleave');
        });
      } else if(type == 'focusout'){
        this.dropdown.addEventListener(type, (evt) => {
           let navElement = evt.target.closest('nav');
          if(navElement.classList.contains('navbar-mobile'))return;
          if (!this.dropdown.contains(evt.relatedTarget) && this.dropdown.classList.contains('open')) {
            this.dropdownHide(this.dropdown,'focusout');
          }
        });
      }
    });

    // Escape from ANYWHERE inside the open panel (not just the trigger —
    // plain content links like a childlink have no keydown listener of
    // their own) closes the nearest open level and returns focus to its
    // trigger. stopPropagation() only when we actually act, so an Escape
    // that finds this level already closed keeps bubbling up to let a
    // parent megamenu close on a second press — walking back one level
    // per press instead of jumping out entirely.
    this.dropdown.addEventListener('keydown', (evt) => {
      if (evt.key !== 'Escape') return;
      let navElement = evt.target.closest('nav');
      if (navElement?.classList.contains('navbar-mobile')) return;
      if (!this.dropdown.classList.contains('open')) return;
      evt.preventDefault();
      evt.stopPropagation();
      this.dropdownHide(this.dropdown, 'escape');
      this.toggleCTA.focus();
    });

    this.bindEvents();
  }

  bindEvents(){
    const closeBtn = this.dropdown.querySelector('.close-submenu');
    if (closeBtn) {
      closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();evt.stopImmediatePropagation();
        this.toggleDropdown(evt);
      });
    }
  }

  toggleDropdown(evt) {
    let dropdownParent = evt.currentTarget.closest('[is="drop-down"]');
    this.dropdownToggle(dropdownParent);
  }

  dropdownToggle(dropdownParent) {
    setTimeout(() => {
      dropdownParent.classList.contains('open')
        ? this.dropdownHide(dropdownParent, 'dropdownToggle')
        : this.dropdownShow(dropdownParent);
    }, 50);
  }

  dropdownShow(dropdownParent) {
    if (!dropdownParent) {
      return;
    }
    this.toggleCTA.setAttribute('aria-expanded', true);
    dropdownParent.classList.add('open');

    // global-header.css:606 force-shows a stale `.first-open` child's panel
    // via CSS regardless of that child's own `.open`/aria-expanded state.
    // Clear any leftover marker from a previous mouse hover so a keyboard
    // user never sees a panel that ARIA says is collapsed.
    dropdownParent.querySelectorAll('.first-open').forEach((el) => {
      if (!el.classList.contains('open')) el.classList.remove('first-open');
    });

    this.recalcMenuHeight(dropdownParent);
  }

  // .dropdown-menu--main has overflow:hidden and no explicit height of its
  // own — a nested level-1 item's panel (.dropdown-menu--submenu > li) gets
  // clipped unless this runs, regardless of whether it was opened by mouse,
  // click or keyboard. No-ops for a top-level megamenu (it has no ancestor
  // .dropdown-menu--main to resize) and for items with no nested panel.
  recalcMenuHeight(dropdownParent) {
    const submenu = dropdownParent.querySelector(':scope > .dropdown-menu');
    const mainMenu = dropdownParent.closest('.dropdown-menu--main');
    if (!submenu || !mainMenu) return;

    // Reset BEFORE measuring — submenu has min-height:100% of mainMenu, so
    // measuring while a stale (taller) height from a previously-hovered
    // sibling is still applied inflates the reading, which then never
    // shrinks back down as the mouse moves across shorter items — the
    // panel keeps stretching to whatever the tallest item ever measured.
    mainMenu.style.height = '';
    const height = submenu.offsetHeight;
    mainMenu.style.height = `${height + 50}px`;
    mainMenu.style.setProperty('--menu-height', `${height + 50}px`);
  }

  dropdownHide(dropdownParent,from='none') {

    if (!dropdownParent) return;

    this.toggleCTA.setAttribute('aria-expanded', false);
    dropdownParent.classList.remove('open');

    const childDropdowns = dropdownParent.querySelectorAll('.dropdown.open');
    if (childDropdowns.length > 0) {
      childDropdowns.forEach((child) => {
        child.classList.remove('open');
        child.querySelector('.dropdown-toggle').setAttribute('aria-expanded', false);
      });
    }
  }

  childOnFocusChange(dropdownParent) {
    // :scope > .dropdown.open — direct children only. querySelectorAll
    // without :scope matches at ANY depth, so a nested child that's
    // legitimately still open (e.g. a level-1 category open inside its
    // still-open top-level megamenu) was being misread as a "sibling" of
    // the megamenu itself and closed on every focus change within it.
    const openDropdowns = Array.from(dropdownParent.parentElement.querySelectorAll(':scope > .dropdown.open'));
    const currentIndex = openDropdowns.indexOf(dropdownParent);
    openDropdowns.forEach((ele) => {
      const eleIndex = openDropdowns.indexOf(ele);
      if (eleIndex !== currentIndex) {
        this.dropdownHide(ele);
      }
    });
  }
}
const allDropdowns = document.querySelectorAll('[is="drop-down"]');
allDropdowns.forEach((ele) => {
  new Dropdown(ele);
});

// Arrow-key navigation for the desktop megamenu (ADA). One delegated
// listener per navbar rather than per Dropdown instance, since moving
// between TOP-LEVEL items has to reach across sibling megamenus that don't
// share a single instance. Everything below keys off [is="drop-down"],
// role="menuitem"/[role], .dropdown-menu-column and DOM position — never a
// fixed item count — so it keeps working however many links a menu ends up
// with in the admin.
document.querySelectorAll('.desktop-navbar').forEach((navbar) => {
  navbar.addEventListener('keydown', (evt) => {
    const key = evt.key;
    if (!['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(key)) return;

    // Featured-column cards (submenu-items-dkp) are Tab-only by design —
    // each renders two links to the same href, and they aren't wrapped in
    // an <li> the way every other menu item is, so without this they'd
    // fall through to the generic handling below scoped to their
    // *enclosing category's* siblings instead of being left alone.
    if (evt.target.closest('.side-images')) return;

    const focusable = (container) => container?.querySelector('a[href], button') || null;

    // .dropdown-menu-column lays a single flat childlink list into two
    // visual columns via CSS column-count — there's no left/right grouping
    // in the DOM to key off, so this compares actual on-screen position
    // instead, same idea as the browser's own left/right roving-focus for
    // CSS grid/columns.
    const focusColumnNeighbor = (li, direction) => {
      const column = li.closest('.dropdown-menu-column');
      if (!column) return false;
      const items = Array.from(column.children).filter((el) => el.matches('li') && focusable(el));
      const currentRect = li.getBoundingClientRect();
      let best = null;
      let bestScore = Infinity;
      items.forEach((item) => {
        if (item === li) return;
        const rect = item.getBoundingClientRect();
        const dx = rect.left - currentRect.left;
        if (direction > 0 ? dx <= 0 : dx >= 0) return; // only consider items actually in the requested direction
        const dy = Math.abs(rect.top - currentRect.top);
        const score = Math.abs(dx) + dy * 2; // favor the same row, but still land on the nearest column item
        if (score < bestScore) { bestScore = score; best = item; }
      });
      if (!best) return false;
      focusable(best).focus();
      return true;
    };

    // The "Shop All"/heading link at the top of a level-1 panel isn't
    // wrapped in its own <li> like sibling childlinks are — its nearest
    // <li> ancestor is the megamenu's own [is="drop-down"] <li>, same as
    // the trigger itself. A real childlink's nearest <li> is its OWN
    // <li role="menuitem">, a different element — that's what tells the
    // two apart.
    const enclosingDropdownLi = evt.target.closest('[is="drop-down"]');
    const isHeadingLink = evt.target.tagName === 'A'
      && enclosingDropdownLi
      && evt.target !== enclosingDropdownLi.querySelector(':scope > .dropdown-toggle')
      && evt.target.closest('li') === enclosingDropdownLi;

    // Heading link + childlinks + their thirdlinks read as ONE continuous
    // Up/Down sequence, matching visual/DOM order — querySelectorAll on the
    // panel already walks into a childlink's nested thirdlink <ul> in the
    // right order for free, so Down from a childlink-with-thirdlinks goes
    // into them instead of skipping to the next childlink, and Up from the
    // very first childlink lands back on the heading link. Featured column
    // is excluded (Tab-only, same reasoning as the early return above).
    const inColumn = isHeadingLink || !!evt.target.closest('.dropdown-menu-column');
    if (inColumn) {
      const panel = enclosingDropdownLi.querySelector(':scope > .dropdown-menu');
      const flatItems = Array.from(panel.querySelectorAll('a[href]'))
        .filter((a) => !a.closest('.side-images'));
      const idx = flatItems.indexOf(evt.target);

      if (key === 'ArrowDown' || key === 'ArrowUp') {
        evt.preventDefault();
        if (idx === -1 || flatItems.length < 2) return;
        flatItems[(idx + (key === 'ArrowDown' ? 1 : -1) + flatItems.length) % flatItems.length].focus();
      } else if (key === 'Home') {
        evt.preventDefault();
        flatItems[0]?.focus();
      } else if (key === 'End') {
        evt.preventDefault();
        flatItems[flatItems.length - 1]?.focus();
      } else if (key === 'ArrowLeft' || key === 'ArrowRight') {
        // Left/right still respects visual column position for a plain
        // childlink (see focusColumnNeighbor below) — only defined once we
        // know we're on a real childlink <li>, not the heading link itself.
        const li = evt.target.closest('.dropdown-menu-column li');
        const wentSideways = li && focusColumnNeighbor(li, key === 'ArrowRight' ? 1 : -1);
        if (wentSideways) {
          evt.preventDefault();
        } else if (key === 'ArrowLeft') {
          evt.preventDefault();
          enclosingDropdownLi.__dropdown.dropdownHide(enclosingDropdownLi, 'arrowkey');
          enclosingDropdownLi.__dropdown.toggleCTA.focus();
        }
      }
      return;
    }

    const li = evt.target.closest('li');
    if (!li) return;

    const isTopLevel = li.parentElement.hasAttribute('data-menu-afterload');

    const siblingItems = () => Array.from(li.parentElement.children)
      .filter((el) => el.matches('li') && focusable(el));

    const focusSibling = (direction) => {
      const siblings = siblingItems();
      const idx = siblings.indexOf(li);
      if (idx === -1 || siblings.length < 2) return;
      focusable(siblings[(idx + direction + siblings.length) % siblings.length]).focus();
    };

    const focusEdge = (end) => {
      const siblings = siblingItems();
      if (!siblings.length) return;
      focusable(end === 'first' ? siblings[0] : siblings[siblings.length - 1]).focus();
    };

    // Down/Right on a trigger opens its panel (if closed) and moves focus
    // inside — to the heading/"Shop All" link when present, else the first
    // category or childlink, whichever the panel actually contains.
    const openAndFocusFirst = () => {
      const dd = li.__dropdown;
      if (!dd) return false;
      if (!li.classList.contains('open')) dd.dropdownShow(li);
      const panel = li.querySelector(':scope > .dropdown-menu');
      const first = focusable(panel);
      if (first) first.focus();
      return true;
    };

    if (isTopLevel) {
      if (key === 'ArrowRight') { evt.preventDefault(); focusSibling(1); }
      else if (key === 'ArrowLeft') { evt.preventDefault(); focusSibling(-1); }
      else if (key === 'Home') { evt.preventDefault(); focusEdge('first'); }
      else if (key === 'End') { evt.preventDefault(); focusEdge('last'); }
      else if (key === 'ArrowDown') { if (openAndFocusFirst()) evt.preventDefault(); }
      return;
    }

    // Only level-1 category triggers reach here now (top-level handled
    // above, childlinks/thirdlinks/heading handled by the `inColumn` branch
    // earlier): Down/Up between categories, Right opens the focused one and
    // moves inside, Left closes it (or walks up to the enclosing megamenu
    // if this one isn't open) — same "one level per press" as Escape.
    if (key === 'ArrowDown' || key === 'ArrowUp') {
      evt.preventDefault();
      focusSibling(key === 'ArrowDown' ? 1 : -1);
    } else if (key === 'Home') {
      evt.preventDefault(); focusEdge('first');
    } else if (key === 'End') {
      evt.preventDefault(); focusEdge('last');
    } else if (key === 'ArrowRight') {
      if (openAndFocusFirst()) evt.preventDefault();
    } else if (key === 'ArrowLeft') {
      if (li.__dropdown && li.classList.contains('open')) {
        evt.preventDefault();
        li.__dropdown.dropdownHide(li, 'arrowkey');
        li.__dropdown.toggleCTA.focus();
      } else {
        const parentDropdownLi = li.parentElement.closest('[is="drop-down"]');
        if (parentDropdownLi) {
          evt.preventDefault();
          parentDropdownLi.__dropdown.dropdownHide(parentDropdownLi, 'arrowkey');
          parentDropdownLi.__dropdown.toggleCTA.focus();
        }
      }
    }
  });
});

// First-open submenu highlight — runs once so each <li> gets exactly one listener.
document.querySelectorAll('.dropdown-menu--submenu li:not(.h5)').forEach((item) => {
  item.addEventListener('mouseenter', function () {
    this.closest('ul').querySelectorAll('li:not(.h5)').forEach((m) => m.classList.remove('first-open'));
    this.classList.add('first-open');
  });
});

/**
* background Overlay
*/
class SiteOverlay extends HTMLElement{
  constructor() {
    super();
    this.overlayElement = document.querySelector('#site-overlay');
    this.bindEvents();
  }

  /**
   * bind Click and Touchend event to this custom component
   */
  bindEvents(){
    this.addEventListener('click', this.onClick.bind(this));
    this.addEventListener('touchend', this.onClick.bind(this));
  }

  /**
   * Click event of Site overlay
   */
  onClick(){
    const closeSearch = document.querySelector('search-modal')?.classList.contains("open__modal");
    if(closeSearch) document.querySelector('.search-modal__close-button')?.dispatchEvent(new Event('click'));
    document.querySelectorAll(".predictive-search").forEach((target) => {
      target.classList.remove("show-results");
    });
    this.hideOverlay();
  }

  /**
   * Show Overlay
   */
   showOverlay(){
    document.querySelector('#site-overlay').classList.add('overlay--body');
    document.querySelector('#site-overlay').setAttribute('aria-hidden', false);
    document.body.classList.add('scroll-fixed');
  }

  /**
   * Hide overlay
   */
  hideOverlay(){
    const openDrawers = document.querySelectorAll('custom-drawer.open');
    openDrawers.forEach(drawer => {
      drawer.closeDrawer();
    });
    
    document.querySelector('#site-overlay').classList.remove('overlay--body');
    document.querySelector('#site-overlay').setAttribute('aria-hidden', true);
    document.body.classList.remove('scroll-fixed');
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.onClick.bind(this));
    this.removeEventListener('touchend', this.onClick.bind(this));
  }
}
customElements.define("site-overlay", SiteOverlay, {
  once: true
});

/**
* Custom Video
*/
class CustomVideo extends HTMLElement {
  constructor() {
    super();

    this.type = this.getAttribute('data-video-type');
    this.videoURL = this.getAttribute('data-video-id');
    this.placementType = this.getAttribute('data-placement');
    this.videoStatus = 'empty';

    this.container = this.closest('.custom-video-container');
    this.videoModal = document.getElementById('PopupModal-video');
    this.contentSection = this.querySelector('.content-section');
    this.videoSection = this.querySelector('.video-section');
    if(this.placementType == 'popup') this.videoSection = this.videoModal.querySelector('.video-container');
    this.triggerVideo = this.container.querySelector('[data-trigger-video]');

    this.bindEvents();
  }

  connectedCallback() {
    // Connected callback
  }

  // Clean up event listeners
  disconnectedCallback() {
    if (this.triggerVideo) {
      this.triggerVideo.removeEventListener('click', this.toggleView.bind(this));
    }
  }

  /**
   * Binding events on the video button
   */
  bindEvents(){
    if(this.querySelector('[data-postervideo]')) this.managePosterVideos();
    if(this.triggerVideo) this.triggerVideo.addEventListener('click', this.toggleView.bind(this));
    if(this.dataset.autoplay == 'true' && !this.closest('swiper-slide')) this.autoplayOnInit();
    // if(this.placementType == 'grid') this.addEventListener('focusout', this.focusOut.bind(this));
  }

  /**
   * Focus Out Event to close drawer
   *
   * @param {event} Event instance
   */
  focusOut(_event){
    if(this.container.classList.contains('playing--video')) this.toggleView();
  }

  /**
   * Toggle of video modal
   */
  toggleView(event){
    if(event) event.preventDefault();
    const isOpen = this.container.classList.contains('playing--video');
    if (isOpen) {
      this.hideContent();
    } else {
      this.showContent();
    }
  }

  /**
   * Show panel content
   */
  showContent() {
    if (this.videoStatus === 'empty' || this.placementType === 'popup') {
      const videoElement = this._buildVideo();
      videoElement.id = 'custom-video' + Math.floor((Math.random() * 100) + 1);
      this.videoSection.appendChild(videoElement);
      this.videoStatus = 'loaded';

      setTimeout(() => {
        if (this.placementType === 'popup') {
          this.openModal(this.videoModal, this.triggerVideo);
        } else {
          this.container.classList.add('playing--video');
        }
        this.videoSection.setAttribute('aria-hidden', 'false');
        if (this.placementType !== 'popup') this.pauseOnVisibilityChange(videoElement);
        this._playVideo(videoElement);
      }, 700);
    } else {
      const videoElement = this.querySelector('.video-section video') || this.querySelector('.video-section iframe');
      this.container.classList.add('playing--video');
      this._playVideo(videoElement);
    }
  }

  /**
   * Hide panel content
   */
  hideContent() {
    this.container.classList.remove('playing--video');
    this.videoSection.setAttribute('aria-hidden', 'true');
    const videoElement = this.querySelector('.video-section video') || this.querySelector('.video-section iframe');
    if (videoElement) {
      this._pauseVideo(videoElement);
    }
  }

  _playVideo(video){
    if(this.type == 'local'){
      video.play();
    }else if(this.type == 'youtube'){
      video.contentWindow.postMessage('{"event":"command","func":"playVideo","args":""}', '*');
    }
  }

  _pauseVideo(video) {
    if (this.type === 'local') {
      video.pause();
    } else if (this.type === 'youtube') {
      video.contentWindow.postMessage('{"event":"command","func":"pauseVideo","args":""}', '*');
    }
  }

  /**
   * 
   * Create the video html element
   */
  _buildVideo(){
    let video;

    if(this.type == 'local'){
      video = document.createElement('video');
      video.src = this.videoURL;
      if(this.dataset.controls == 'true') video.controls = this.dataset.controls;
      if(this.dataset.muted == 'true') video.muted = this.dataset.muted;
      if(this.dataset.autoplay == 'true') video.autoplay = this.dataset.autoplay;
      if(this.dataset.loop == 'true') video.loop = this.dataset.loop;
      video.playsInline = true;
    }else if(this.type == 'youtube'){
      const videoID = this.videoURL.replace('https://www.youtube.com/watch?v=', '');
      video = document.createElement( "iframe" );
      video.setAttribute( "allowfullscreen", "" );
      video.setAttribute( "frameborder", "0" );
      video.setAttribute( "allow", "autoplay");
      video.setAttribute( "src", "https://www.youtube.com/embed/"+ videoID +`?rel=0&showinfo=0&autoplay=${this.dataset.autoplay}&muted=${this.dataset.muted}&controls=${this.dataset.controls}&loop=${this.dataset.loop}&playlist=${videoID}&enablejsapi=1&origin=${window.origin}` );
    }
    return video;
  }

  /**
   * Pause video on scroll when out of viewport
   *
  */
  pauseOnVisibilityChange(videoEle){
    const scrollObserver = new IntersectionObserver((entries) => { 
      if(videoEle == undefined || videoEle.contentWindow == null){
        scrollObserver.disconnect();
        return;
      }

      entries.forEach(entry => {
        if(entry.intersectionRatio == 0){
         this._pauseVideo(videoEle)
        }
      });
    });
    scrollObserver.observe(videoEle);
  }

  /**
   * Poster Video Autoplay
   *
  */
  managePosterVideos(){
    const posterVideo = this.querySelector('[data-postervideo]');
    if(!posterVideo) return;

    const videoIntersection = (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting && entry.target) {
          const sourceTag = document.createElement('source');
          sourceTag.src = entry.target.dataset.src;
          sourceTag.type = "video/mp4";
          entry.target.appendChild(sourceTag)
          entry.target.play();
          observerPosterVideo.disconnect();
        }
      }
    };
    const observerPosterVideo = new IntersectionObserver(videoIntersection);
    observerPosterVideo.observe(posterVideo);
  }

  openModal(modal, opener){
    if(modal && opener != undefined) modal.show(opener);
  }
}
customElements.define("custom-video", CustomVideo);


/**
* Modal Dialogue
*
*/
class ModalDialog extends HTMLElement {
  constructor() {
    super();

    this.modal = this.querySelector('.modal');
    this.modalOpeners = [...document.querySelectorAll(`[data-modal-opener="${this.id}"]`)];
    this.closeBtn = this.querySelector('[id^="ModalClose-"]');
    this.videoContainer = this.querySelector('.video-container');
    if (this.videoContainer && this.classList.contains('promotional-video-modal')) {
      this.initialVideoContent = this.videoContainer.innerHTML;
    }

    // Prevent clicks inside modal from bubbling to drawer
    this.modal?.addEventListener('click', (evt) => {
      evt.stopPropagation();
    });
  }

  connectedCallback() {
    this.modalOpeners.forEach((opener)=>{
      opener.addEventListener('click', (evt)=>{
        evt.preventDefault();
        this.show(opener);
      });
    });

    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (evt) => {
        evt.preventDefault();
        evt.stopPropagation();
        this.hide(evt);
      });
    }

    this.querySelector('.modal')?.addEventListener('click', (evt) => {
      if (evt.target.classList.contains('modal')) {
        evt.stopPropagation();
        this.hide(evt);
      }
    });

    this.addEventListener('keyup', (evt) => {
      if (evt.code && evt.code.toUpperCase() === 'ESCAPE') {
        evt.stopPropagation();
        this.hide(evt); 
      }
    });
  }

  // Clean up event listeners
  disconnectedCallback() {
    this.modalOpeners.forEach((opener) => {
      opener.removeEventListener('click', this.show.bind(this));
    });

    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.hide.bind(this));
    }

    this.removeEventListener('click', this.hide);
    this.removeEventListener('keyup', this.hide);
  }

  /**
   * Open the Modal
   * @param {element} opener 
   */
  show(opener) {
    this.openedBy = opener;
    this.modal.classList.add('open');
    this.setAttribute('aria-hidden', false);
    
    const pdpStickyEl = document.querySelector('.pdp-sticky');
    if (pdpStickyEl) {
      pdpStickyEl.classList.add('size-modal-open');
    }
    
    if (this.classList.contains('promotional-video-modal') && this.videoContainer) {
      if (!this.videoContainer.innerHTML) {
        this.videoContainer.innerHTML = this.initialVideoContent;
      }
      this.querySelector('video').setAttribute('autoplay','true');
    }

    // Skip overlay for size chart button in product page
    const productPageWrapper = opener?.closest('.pdp-sticky .variant-section-wrapper');
    const isModalOpen = productPageWrapper?.querySelector('#sizechart-popup .modal.open');
    if (!productPageWrapper || !isModalOpen) {
      SiteOverlay.prototype.showOverlay();
    }
    Utility.trapFocus(this);
    Utility.forceFocus(this.closeBtn);
  }

  /**
   * Close Modal Dialogue
   */
  hide(event) {
    if (event) {
      event.stopPropagation();
    }
    
    this.modal.classList.remove('open');
    this.setAttribute('aria-hidden', true);

    const pdpStickyEl = document.querySelector('.pdp-sticky');
    if (pdpStickyEl) {
      pdpStickyEl.classList.remove('size-modal-open');
    }

    // Only hide overlay if no drawers are open
    const openDrawers = document.querySelectorAll('custom-drawer.open');
    if(openDrawers.length === 0){
      SiteOverlay.prototype.hideOverlay();
    }

    Utility.removeTrapFocus(this.openedBy);
    if(this.id == 'PopupModal-video' || this.classList.contains('promotional-video-modal')) this.querySelector('.video-container').innerHTML = '';
  }
}
customElements.define('modal-dialog', ModalDialog);

/**
   * Manage Custom Notification Trigger
   *
   */
class ToastNotification extends HTMLElement {
  constructor() {
    super();

    this.container = this.querySelector('.snotifyToast');
    this.closeBtn = this.querySelector('.snotifyToast__close');
    this.animateNotifcation = null;

    // Set ARIA role for accessibility
    this.container.setAttribute('role', 'alert');
  }

  connectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        this.hide();
      });
    }
  }

  disconnectedCallback() {
    if (this.closeBtn) {
      this.closeBtn.removeEventListener('click', this.hide.bind(this));
    }
  }

  /**
   * Notification Type
   * @param {string} title 
   * @param {string} body 
   * @param {object} options 
   */
  updateNotification(title, body, options){
    this.querySelector('.snotifyToast__title').textContent = title;
    this.querySelector('.snotifyToast__body').textContent = body;

    this.container.classList.add('open', `snotify-${options.type}`);
    this._manageToggle(options.timeout);
  }

  /**
   * Duration of notification to hide again
   * @param timeout 
   */
  _manageToggle(timeout) {
    this.progressBar = this.querySelector('[data-toast-pr]');
    const totalFrames = Math.ceil(timeout / 16.67) / 2;
    let frame =0; 
    let width = 0;

    this.animateNotifcation = () => {
      width = (frame / totalFrames) * 100;
      this.progressBar.setAttribute('style', `width: ${width}%`);
      if (frame < totalFrames) {
        frame++;
        requestAnimationFrame(this.animateNotifcation);
      } else {
        this.closeBtn.dispatchEvent(new Event('click'));
      }
    };
    requestAnimationFrame(this.animateNotifcation);
  }

  hide() {
    if (this.animateNotification) {
      cancelAnimationFrame(this.animateNotification);
    }
    this.container.className = 'snotifyToast';
    [this.querySelector('.snotifyToast__title'),this.querySelector('.snotifyToast__body')].forEach(ele => ele.textContent = '');
    this.querySelector("[data-toast-pr]").removeAttribute("style");
  }
}
customElements.define("toast-notification", ToastNotification);
window.notificationEle = document.querySelector('toast-notification');
// notificationEle.updateNotification('Title', 'Body', {
//   type: 'success',
//   timeout: 5000
// });

class CustomDrawer extends HTMLElement {
  constructor() {
      super();
      this.isOpen = false;

      this.openedBy = null;
      this.toggleDrawer = document.querySelectorAll(`[data-toggleDrawer="${this.id}"]`);
      this.closeButtons = this.querySelectorAll('[data-closedrawer]');
      this.forceFocus = this.querySelector('.drawer-header') || this.querySelector('.first-focusable-ele')
  }

  connectedCallback() {
    this.bindEvents()
  }

  disconnectedCallback() {
    this.toggleDrawer.forEach(btn => btn.removeEventListener('click', this.openDrawer()));

    this.closeButtons.forEach(btn =>{
      btn.removeEventListener('click', this.closeDrawer.bind(this));
    })
    this.removeEventListener('keyup', this.onKeyUp.bind(this));
    this.removeEventListener('focusout', this.focusOut.bind(this));
  }

  bindEvents(){
    this.toggleDrawer.forEach(btn => {
      btn.setAttribute('aria-expanded', false);
      btn.addEventListener('click', (evt) => {
 
        evt.preventDefault();
        if(btn.dataset.for == 'collection-sorting'){
          this.classList.add('collection-sorting');
          setTimeout(() => {
            (this.isOpen) ? this.closeDrawer(evt) : this.openDrawer(evt.currentTarget);
          }, 500);
        }else{
          (this.isOpen) ? this.closeDrawer(evt) : this.openDrawer(evt.currentTarget);
        }
        
      });
    });

    this.closeButtons.forEach(btn => btn.addEventListener('click', this.closeDrawer.bind(this)));
    if (window.innerWidth > 992) {
      this.addEventListener('keyup', this.onKeyUp.bind(this));
      this.addEventListener('focusout', this.focusOut.bind(this));
    }
  }

  /**
   * Escape Key Press to close drawer when focus is within the container
   */
  onKeyUp(evt) {
    if(evt.code && evt.code.toUpperCase() !== 'ESCAPE') return;
    this.closeDrawer();
  }

  /**
   * Focus Out Event to close drawer
   */
  focusOut(evt){
    if (!this.contains(evt.relatedTarget) && this.open) {
      this.closeDrawer();
    }
  }

// openDrawer(opener) {
//   console.log('openDrawer', opener) ;
//   if(window.globalSpace.templateSuffix == 'waste-management-2025' && opener.closest('[data-product-container]')){
//     let productContainer =   opener.closest('[data-product-container]');
//     //console.log('waste-management-2025' , productContainer) ;
//     let variantSelector = productContainer.querySelector('[data-selectcontainer]');
//     if (variantSelector && variantSelector.querySelector('.selected')) {
//       console.log('waste-management-2025' , variantSelector) ;
//       let selectedOption = variantSelector.querySelector('.selected');
//       if(selectedOption.hasAttribute('default-selected')){
//        console.log('default-selected') ;
//        let selectBox = selectedOption.closest('.select');
//        if(selectBox){
//         selectBox.classList.add('error-please-select');
//         setTimeout(()=>{
//           selectBox.classList.remove('error-please-select');
//         },2400);
//        }
//         return;
//       }
//     }
      
//   }
//   this.resetOtherDrawers();
//   this.isOpen = true;
//   this.openedBy = opener || null;
//   this.classList.add('open');
//   this.setAttribute('aria-hidden', 'false');
//   this.openedBy?.setAttribute('aria-expanded', true);

//   if (!(this.id === 'mobile-nav-drawer' && document.body.id.includes('gfr-content'))) {
//     SiteOverlay.prototype.showOverlay();
//   }
//   document.body.classList.add('scroll-fixed');
//   Utility.trapFocus(this, this.forceFocus);
//   Utility.forceFocus(this.forceFocus);
// }

validateVariantSelection(opener) {
  if (
    !window.globalSpace ||
    window.globalSpace.templateSuffix !== "waste-management-2025" ||
    !opener?.closest
  ) return true; // ✅ allow opening if not waste-management template

  const productContainer = opener.closest("[data-product-container]");
  const variantSelector  = productContainer?.querySelector("[data-selectcontainer]");
  const selectedOption   = variantSelector?.querySelector(".selected");

  if (selectedOption?.hasAttribute("default-selected")) {
    const selectBox = selectedOption.closest(".select");
    if (selectBox) {
      selectBox.classList.add("error-please-select");
      setTimeout(() => selectBox.classList.remove("error-please-select"), 2400);
    }
    return false; // ❌ block drawer opening
  }

  return true; // ✅ safe to open drawer
}

openDrawer(opener) {
  try {
    // ✅ Block drawer if validation fails
    if (!this.validateVariantSelection(opener)) return;

    // ✅ Drawer logic
    this.resetOtherDrawers();
    this.isOpen = true;
    this.openedBy = opener || null;

    this.classList.add("open");
    this.setAttribute("aria-hidden", "false");
    this.openedBy?.setAttribute("aria-expanded", "true");

    if (!(this.id === "mobile-nav-drawer" && document.body.id.includes("gfr-content"))) {
      SiteOverlay.prototype.showOverlay();
    }

    document.body.classList.add("scroll-fixed");
    Utility.trapFocus(this, this.forceFocus);
    Utility.forceFocus(this.forceFocus);

  } catch (err) {
    console.error("🚨 Error in openDrawer:", err);
  }
}


closeDrawer() {
    this.isOpen = false;
    this.classList.remove('open');
    this.setAttribute('aria-hidden', 'true');
    this.openedBy?.setAttribute('aria-expanded', false);
    setTimeout(() => {
      this.classList.remove('collection-sorting');
    }, 1000);

    if (!(this.id === 'mobile-nav-drawer' && document.body.id.includes('gfr-content'))) {
      SiteOverlay.prototype.hideOverlay();
    }
    document.body.classList.remove('scroll-fixed');
    Utility.removeTrapFocus(this.openedBy);
    this.openedBy = null;
    if(document.querySelector(".navbar-mobile-slide").querySelectorAll(".open").length > 0){
      document.querySelector(".navbar-mobile-slide").querySelectorAll(".open").forEach((target) => {
        target.classList.remove("open");
      });
    } 
}

resetOtherDrawers(){
  const openDrawers = document.querySelectorAll('custom-drawer.open');
  openDrawers.forEach(drawer => {
    drawer.closeDrawer();
  });
  
}
}
customElements.define('custom-drawer', CustomDrawer);



// swiper-slider-component.js (defer, declared first) has already run and initialized all
// swiper-containers by the time global.js executes. Mark collection sliders immediately
// so the opacity:0 pre-init CSS in theme.liquid releases them.
// The event listener below handles any sliders injected dynamically after page load.
document.querySelectorAll('swiper-container.card-carousel_slider, .topbar swiper-container').forEach(function(el) {
  el.setAttribute('data-init', '');
});
document.addEventListener('swiperinit', function(e) {
  if (e.target.tagName && e.target.tagName.toLowerCase() === 'swiper-container') {
    if (e.target.classList.contains('card-carousel_slider') || e.target.closest('.topbar')) {
      e.target.setAttribute('data-init', '');
    }
  }
});

const swiperSliders = document.querySelectorAll('swiper-container');
swiperSliders.forEach(swiperEle => {
  swiperEle.addEventListener('swiperslidechange', (event) => {
    const index = event.detail[0].activeIndex;
    const swipDirection = event.detail[0].swipeDirection;
    const prevSlide = swiperEle.querySelector(`swiper-slide[data-index="${swipDirection == 'prev' ? index+2 : index}"]`);
    const activeSlide = swiperEle.querySelector(`swiper-slide[data-index="${index+1}"]`);

    // Pause previous slide video if available
    if(prevSlide && prevSlide.querySelector('.custom-video-container.playing--video')){
      let customElement = prevSlide.querySelector('custom-video');
      let videoEle = customElement.querySelector('video') || customElement.querySelector('iframe');
      if(videoEle && customElement.dataset.controls == 'true') {
        customElement._pauseVideo(videoEle);
      }
    }

    // Play active slide video if available
    if(prevSlide && activeSlide.querySelector('.custom-video-container')){
      let customElement = activeSlide.querySelector('custom-video');
      if(customElement.videoStatus == 'empty'){
        customElement.toggleView();
      }else{
        let videoEle = customElement.querySelector('video') || customElement.querySelector('iframe');
        if(videoEle) customElement._playVideo(videoEle);
      }
    }
  });
});


if(document.querySelectorAll("#customerLoginFor-time").length){
  const InputEmail = document.querySelector('#gfr-email');
  const InputPass = document.querySelector('#gfe-password');
  const submitBtn = document.querySelector('.block__form-login');

  function checkFields() {
    if (InputEmail.value.trim() === '' || InputPass.value.trim() === '') {
      submitBtn.disabled = true;
    } else {
      submitBtn.disabled = false;
    }
  }
  InputEmail.addEventListener('input', checkFields);
  InputPass.addEventListener('input', checkFields);
}

// Plain items (no nested dropdown-menu, e.g. "Back To School" inside the
// submenu list) reset the panel back to its natural height on hover. Items
// that DO have a dropdown are sized by Dropdown#recalcMenuHeight instead,
// triggered from dropdownShow() so it covers mouse, click and keyboard alike.
document.querySelectorAll('.dropdown-menu--submenu > .nav-item').forEach((item) => {
  if (item.querySelector(':scope > .dropdown-menu')) return;
  const mainMenu = item.closest('.dropdown-menu--main');
  if (!mainMenu) return;
  item.addEventListener('mouseenter', () => {
    mainMenu.style.height = 'auto';
  });
});

document.addEventListener('DOMContentLoaded', () => {
  const dropdownLists = document.querySelectorAll('.more-sports .dropdown-menu');
  dropdownLists.forEach(dropdownList => {
      const parentContainer = dropdownList.closest('.megamenu'); // Find the closest parent with this class
      if (parentContainer) {
          dropdownList.addEventListener('mouseenter', () => {
              parentContainer.classList.add('hovered-parent');
          });
          dropdownList.addEventListener('mouseleave', () => {
              parentContainer.classList.remove('hovered-parent');
          });
      }
  });
});


// JS for megamenu height adjustment
window.addEventListener("load", () => {
  const screenWidth = window.innerWidth;
  const screenHeight = window.innerHeight;

  if (screenWidth > 1200 && screenHeight < 900) {
    document.querySelectorAll(".desktop-navbar .navbar-nav > .megamenu > .dropdown-menu")
      .forEach(menu => {
        menu.classList.add("small-height");
      });
  }
});

customElements.define('complementary-variant-select', class extends HTMLElement {
  constructor() {
    super();
    document.addEventListener('DOMContentLoaded', () => {
      this.init();
    });
  }

  init(){
    this.productData = JSON.parse(this.querySelector('script[type="text/json"]').textContent);
    this.dataParents = this.querySelectorAll('[data-option-parent]');
    this.formId = this.getAttribute('data-form-id');
    this.form = document.getElementById(this.formId);
    this.options = this.productData.options;
    this.selectOptionsWrapper = this.form.querySelector('[data-select-options]');
    this.selectOptionsButton = this.selectOptionsWrapper.querySelector('button');
    this.addToCartWrapper = this.form.querySelector('[data-add-to-cart]');
    this.addCartButton = this.form.querySelector('[data-add-to-cart] button');
    this.selectOptionsContent = this.form.querySelector('.complementary-variant-select__content');
    this.ClosePopUp = this.selectOptionsWrapper.querySelector('.closePairPopup');
    
    this.checkAndUpdateVariantAvailability();
    this.updateColorSwatches();

    this.querySelectorAll('input[type="radio"]').forEach(input => {
      input.addEventListener('change', (evt) => {
        const optionIndex = evt.target.closest('[data-option-parent]').dataset.optionIndex = evt.target.dataset.optionIndex;
        this.getCurrentVariant();
        this.onVariantChange();
        this.checkAndUpdateVariantAvailability();
      });
    });
    this.selectOptionsButton.addEventListener('click', (evt) => {
      evt.preventDefault();
      evt.stopPropagation();
      this.selectOptionsContent.classList.toggle('active');
      this.addToCartWrapper.classList.toggle('d-none');
      this.selectOptionsWrapper.classList.toggle('d-none');
    });
    document.querySelectorAll(".closePairPopup").forEach(each=>{
      each.addEventListener("click", function(evt){
         evt.preventDefault();
      evt.stopPropagation();
        each.closest('.complementary-variant-select__content').classList.remove('active')
           each.closest('form').querySelector('[data-add-to-cart]').classList.add('d-none')
           each.closest('form').querySelector('[data-select-options]').classList.remove('d-none')
      })
    })
  }

  getCurrentVariant(){ 
    let selectedOptions = [];
    this.dataParents.forEach(parent => {
      const selectedInput = parent.querySelector('input[type="radio"]:checked');
      if(selectedInput){
        selectedOptions.push(selectedInput.value);
      }
    });
    let selectionOptionTitlte = selectedOptions.join(' / ');
    this.selectedOptions = selectedOptions;
    this.productData.variants.forEach(variant => {
      if(variant.title.toLowerCase() == selectionOptionTitlte.toLowerCase()){
        this.currentVariant = variant;
        this.form.querySelector('input[name="id"]').value = variant.id;
        this.form.querySelector('[data-add-to-cart]').querySelector('button').classList.remove('disabled')
        this.form.querySelector('[data-add-to-cart]').querySelector('button').removeAttribute('disabled')
      }
    });
    return this.currentVariant;
  }

  onVariantChange(){
    if(this.currentVariant && this.currentVariant.available){
      this.addCartButton.querySelector('[data-add-to-cart-text]').textContent = '+ Add To Cart';
      this.addCartButton.disabled = false;
    }else if(this.currentVariant){
      this.addCartButton.querySelector('[data-add-to-cart-text]').textContent = 'Sold Out';
      this.addCartButton.disabled = true;
    }
  }

  updateColorSwatches() {
    const colorSwatchContainer = this.querySelector('.promo-size-row');
    if (!colorSwatchContainer || typeof Utility === 'undefined' || typeof Utility.getSwatchStyle !== 'function') return;
    const swatchListEl = document.querySelector('[data-swatches-colorlist-json]');
    if (!swatchListEl) return;
    const colorSwatches = colorSwatchContainer.querySelectorAll('.swatch');
    colorSwatches.forEach(swatch => {
      const input = swatch.querySelector('input[type="radio"]');
      if (!input || !input.dataset.handle) return;
      const swatchStyle = Utility.getSwatchStyle(input.dataset.handle);
      if (swatchStyle) {
        const label = swatch.querySelector('.swatch-label');
        if (label) label.setAttribute('style', swatchStyle);
      }
    });
  }

  checkAndUpdateVariantAvailability() {
    const optionContainers = Array.from(this.querySelectorAll('[data-option-parent]'));
    if (optionContainers.length >= 2) {
      this.updateVariantAvailability();
    }
  }

  updateVariantAvailability() {
    const variants = this.productData.variants;
    if (!variants) return;
    const selectedOptions = this.selectedOptionValues;
    if (!selectedOptions) return;

    const optionContainers = Array.from(this.querySelectorAll('[data-option-parent]'));
    optionContainers.forEach((container, optionIndex) => {
      const radioInputs = container.querySelectorAll('input[type="radio"]');
      if (radioInputs.length > 0) {
        this.updateRadioInputsAvailability(radioInputs, variants, selectedOptions, optionIndex);
      }
    });
  }

  updateRadioInputsAvailability(inputs, variants, selectedOptions, currentOptionIndex) {
    inputs.forEach(input => {
      const optionValue = input.value;
      const { exists, isAvailable } = this.checkVariantAvailability(variants, selectedOptions, currentOptionIndex, optionValue);
      const swatchLi = input.closest('li');
      this.updateOptionVisibility(input, swatchLi, exists, isAvailable, currentOptionIndex);
    });
  }

  checkVariantAvailability(variants, selectedOptions, currentOptionIndex, optionValue) {
    const exists = variants.some(variant => {
      return variant.options[currentOptionIndex] === optionValue && this.matchesOtherSelectedOptions(variant, selectedOptions, currentOptionIndex);
    });
    const isAvailable = variants.some(variant => {
      return variant.available && variant.options[currentOptionIndex] === optionValue && this.matchesOtherSelectedOptions(variant, selectedOptions, currentOptionIndex);
    });
    return { exists, isAvailable };
  }

  updateOptionVisibility(input, container, exists, isAvailable, optionIndex) {
    const optionParent = container ? container.closest('[data-option-parent]') : null;
    const isColorOption = optionParent ? optionParent.querySelector('.color-swatch') !== null : (optionIndex === 0);
    if (isColorOption) {
      if (container) container.style.display = '';
      this.updateDisabledState(input, isAvailable);
    } else {
      if (exists) {
        if (container) container.style.display = '';
        this.updateDisabledState(input, isAvailable);
      } else {
        if (container) container.style.display = 'none';
      }
    }
  }

  updateDisabledState(input, isAvailable) {
    if (isAvailable) {
      input.classList.remove('disabled');
    } else {
      input.classList.add('disabled');
    }
  }

  matchesOtherSelectedOptions(variant, selectedOptions, currentOptionIndex) {
    return selectedOptions.every((selectedOption, index) => {
      if (index === currentOptionIndex) return true;
      if (!selectedOption || selectedOption === '') return true;
      return variant.options[index] === selectedOption;
    });
  }

  get selectedOptionValues() {
    const optionContainers = Array.from(this.querySelectorAll('[data-option-parent]'));
    return optionContainers.map(container => {
      const checkedInput = container.querySelector('input[type="radio"]:checked');
      return checkedInput ? checkedInput.value : '';
    });
  }

});
document.addEventListener('DOMContentLoaded', () => {
  if(document.body.classList.contains('template-product')){

  /* Add tabIndex to pdp specific elements to support accessibility */
  (function() {
    const observer = new MutationObserver((mutations) => {
      const connectedProducts = document.querySelectorAll('.connected-product a:not([tabindex])');
      connectedProducts.forEach(el => {
        el.setAttribute('tabindex', '0');
        el.setAttribute('role', 'button');
      });

      const swatches = document.querySelectorAll('.swatch:not([tabindex])');
      swatches.forEach(swatch => {
        const radioInput = swatch.querySelector('input[type="radio"]');
        if (radioInput) {
          if (!radioInput.dataset.ariaObserved) {
            radioInput.dataset.ariaObserved = "true";
            radioInput.addEventListener('change', () => swatch.querySelector('label')?.click());
          }
        }
      });

      const sizechartLinks = document.querySelectorAll('.sizechart-link:not([tabindex])');
      sizechartLinks.forEach(el => {
        el.setAttribute('tabindex', '0');
        el.setAttribute('role', 'button');
        el.addEventListener('keydown', (e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            e.target.click();
          }
        });
      });

      const tabcontents = document.querySelectorAll('.tab-content:not([tabindex])');
      tabcontents.forEach(tabcontent => tabcontent.setAttribute('tabindex', '0'));
    });
    observer.observe(document.body, {
        childList: true,
        subtree: true
    });
  })();
  /* Add tabIndex to pdp specific elements to support accessibility */

  document.addEventListener('click', (evt) => {
    // If the click is NOT on or inside a complementary-variant-select element
    if (!evt.target.closest('.complementary-product-card')) {
      const complementaryVariantSelects = document.querySelectorAll('.complementary-variant-select__content.active');
      const dataAddToCart = document.querySelectorAll('[data-add-to-cart]');
      if(dataAddToCart.length > 0){
        dataAddToCart.forEach(el => {
          if(!el.classList.contains('only-default-variant-button')){
            el.classList.add('d-none');
          }
        });
      }
      const dataSelectOptions = document.querySelectorAll('[data-select-options]');
      if(dataSelectOptions.length > 0){
        dataSelectOptions.forEach(el => {
          if(!el.classList.contains('only-default-variant-select-button')){
            el.classList.remove('d-none');
          }
        });
      }
      if(complementaryVariantSelects.length > 0){
        complementaryVariantSelects.forEach(el => {
          el.classList.remove('active');

        });
      }
    }
  });
  }

  /* Add tabIndex to page price blocks to support accessibility */
  (function() {
      const observer = new MutationObserver((mutations) => {
          const prices = document.querySelectorAll('.price-block:not([tabindex]), .price__regular:not([tabindex])');
          prices.forEach(price => price.setAttribute('tabindex', '0'));
      });
      observer.observe(document.body, {
          childList: true,
          subtree: true
      });
  })();
  /* Add tabIndex to page price blocks to support accessibility */
})